/* Receipt PNG generator (canvas) — saved as an image so an illiterate user
   can keep/show it. Big digits, pictogram check, minimal text RU/ES. */

const fmt = (v) => new Intl.NumberFormat("ru-RU").format(v || 0);

const fmtPhone = (p) =>
  p && p.length === 11
    ? `+${p[0]} ${p.slice(1, 4)} ${p.slice(4, 7)}-${p.slice(7, 9)}-${p.slice(9, 11)}`
    : `+${p || ""}`;

export function buildReceiptCanvas({ payment, lang = "ru", mockMode = false }) {
  const W = 720;
  const H = 1000;
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const x = canvas.getContext("2d");
  const es = lang === "es";
  const F = "'Space Grotesk', 'Inter', Arial, sans-serif";

  // background
  x.fillStyle = "#ffffff";
  x.fillRect(0, 0, W, H);

  // header band
  x.fillStyle = "#0587C4";
  x.fillRect(0, 0, W, 148);
  x.fillStyle = "#ffffff";
  x.textAlign = "center";
  x.font = `bold 46px ${F}`;
  x.fillText("NORD", W / 2, 68);
  x.font = `600 26px ${F}`;
  x.fillText(es ? "Recibo de pago • SBP" : "Чек об оплате • СБП", W / 2, 112);

  // green check circle
  const cy = 262;
  x.fillStyle = "#1B8A4B";
  x.beginPath();
  x.arc(W / 2, cy, 66, 0, Math.PI * 2);
  x.fill();
  x.strokeStyle = "#ffffff";
  x.lineWidth = 14;
  x.lineCap = "round";
  x.lineJoin = "round";
  x.beginPath();
  x.moveTo(W / 2 - 30, cy + 2);
  x.lineTo(W / 2 - 6, cy + 26);
  x.lineTo(W / 2 + 36, cy - 24);
  x.stroke();

  // status + amount
  x.fillStyle = "#0F172A";
  x.font = `bold 42px ${F}`;
  x.fillText(es ? "¡Pagado!" : "Оплачено!", W / 2, cy + 118);
  x.fillStyle = "#1B8A4B";
  x.font = `bold 84px ${F}`;
  x.fillText(`${fmt(payment.amount)} ₽`, W / 2, cy + 216);

  // dashed separator
  const sepY = 542;
  x.strokeStyle = "#CBD5E1";
  x.lineWidth = 3;
  x.setLineDash([10, 10]);
  x.beginPath();
  x.moveTo(56, sepY);
  x.lineTo(W - 56, sepY);
  x.stroke();
  x.setLineDash([]);

  // rows
  const rows = [
    [es ? "N.º de operación" : "№ операции", String(payment.operation_number || "—"), true],
    [es ? "Teléfono" : "Телефон", fmtPhone(payment.phone), false],
    [
      es ? "Fecha" : "Дата",
      new Date(payment.paid_at || Date.now()).toLocaleString(es ? "es-ES" : "ru-RU", {
        day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
      }),
      false,
    ],
    ["SBP ID", payment.provider_payment_id || "—", false],
  ];
  let ry = sepY + 74;
  const fitFont = (text, maxW, start, min) => {
    let size = start;
    while (size > min) {
      x.font = `bold ${size}px ${F}`;
      if (x.measureText(text).width <= maxW) break;
      size -= 2;
    }
    return size;
  };
  rows.forEach(([label, value, big]) => {
    x.textAlign = "left";
    x.fillStyle = "#64748B";
    x.font = `500 26px ${F}`;
    x.fillText(label, 64, ry);
    const labelW = x.measureText(label).width;

    const valSize = big ? 44 : 32;
    x.font = `bold ${valSize}px ${F}`;
    const valW = x.measureText(value).width;
    x.fillStyle = "#0F172A";
    if (labelW + valW + 24 <= W - 128) {
      x.textAlign = "right";
      x.fillText(value, W - 64, ry + (big ? 4 : 0));
      ry += big ? 96 : 78;
    } else {
      // value too long for one line: draw it below the label, shrunk to fit
      const size = fitFont(value, W - 128, valSize, 16);
      x.font = `bold ${size}px ${F}`;
      x.textAlign = "left";
      x.fillText(value, 64, ry + 46);
      ry += 46 + 64;
    }
  });

  // bottom dashed separator + thanks
  x.strokeStyle = "#CBD5E1";
  x.setLineDash([10, 10]);
  x.beginPath();
  x.moveTo(56, ry);
  x.lineTo(W - 56, ry);
  x.stroke();
  x.setLineDash([]);
  x.textAlign = "center";
  x.fillStyle = "#0F172A";
  x.font = `bold 34px ${F}`;
  x.fillText(es ? "¡Gracias!" : "Спасибо!", W / 2, ry + 62);

  // SBP color strip
  const colors = ["#1E5BFF", "#00B7FF", "#00C389", "#A6E22E", "#FFD166", "#FF8A3D", "#FF4D4D", "#6A5CFF"];
  const stripW = 320;
  const sw = stripW / colors.length;
  colors.forEach((c, i) => {
    x.fillStyle = c;
    x.fillRect(W / 2 - stripW / 2 + i * sw, ry + 92, sw, 10);
  });

  // DEMO watermark in mock mode
  if (mockMode) {
    x.save();
    x.translate(W / 2, H / 2);
    x.rotate(-Math.PI / 7);
    x.textAlign = "center";
    x.fillStyle = "rgba(15, 23, 42, 0.07)";
    x.font = `bold 170px ${F}`;
    x.fillText("DEMO", 0, 40);
    x.restore();
  }

  return canvas;
}

export async function downloadReceiptPNG({ payment, lang = "ru", mockMode = false }) {
  try {
    if (document.fonts && document.fonts.ready) {
      await document.fonts.ready;
    }
  } catch (e) {
    /* fonts not critical */
  }
  const canvas = buildReceiptCanvas({ payment, lang, mockMode });
  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        resolve(false);
        return;
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `NORD-receipt-${payment.operation_number || payment.id}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
      resolve(true);
    }, "image/png");
  });
}
